import React from 'react';
import { Link } from 'react-router-dom';

const SITE = 'https://tamratdates.com';

// Per-variety EEAT content for product pages. Facts are accurate, general
// variety knowledge only (origin / taste / texture / uses / storage) — NO
// fabricated farm names or medical claims (EEAT-safe, mirrors the category
// explainer pages). Each product page links back to its variety explainer
// (the deeper hub) + the buying guide for internal-link authority.
const VARIETIES = {
  sukari: {
    href: '/sukari-date',
    nameAr: 'تمر سكري', nameEn: 'Sukari dates',
    tagAr: 'ذهبي وحلاوة كراميل', tagEn: 'golden, caramel-sweet',
    ar: {
      about: 'التمر السكري من أشهر التمور السعودية وأكثرها حلاوة، ويُزرع غالباً في منطقة القصيم وسط المملكة المعروفة بجودة تمورها. يتميّز بلونه الذهبي الفاتح وقوامه الطري وطعمه القريب من الكراميل الطبيعي.',
      taste: 'حلاوة عالية ونكهة كراميلية ناعمة، مع قوام طري يذوب في الفم — من أكثر الأنواع تفضيلاً لمحبّي التمر الحلو.',
      uses: 'مثالي للتقديم مع القهوة العربية، كوجبة خفيفة طبيعية، أو في علبة إهداء فاخرة بلونه الذهبي اللافت.',
      store: 'يُحفظ في مكان بارد وجاف، أو في الثلاجة للحفاظ على طراوته لفترة أطول.',
      who: 'لمن يحبّ التمر الطري عالي الحلاوة، ولمن يبحث عن خيار إهداء أنيق.',
    },
    en: {
      about: 'Sukari is one of the most popular and sweetest Saudi date varieties, grown mainly in the Qassim region of central Saudi Arabia, known for the quality of its dates. It is prized for its light-golden colour, soft texture and natural caramel-like flavour.',
      taste: 'Rich sweetness with a smooth, caramel note and a soft melt-in-the-mouth texture — a favourite for anyone who loves a sweet date.',
      uses: 'Perfect served with Arabic coffee, as a natural snack, or in a premium gift box thanks to its striking golden colour.',
      store: 'Keep in a cool, dry place, or refrigerate to preserve its softness for longer.',
      who: 'For those who love a soft, sweet date and for an elegant gifting choice.',
    },
  },
  ajwa: {
    href: '/ajwa-date',
    nameAr: 'تمر عجوة المدينة', nameEn: 'Ajwa dates',
    tagAr: 'داكن وفاخر', tagEn: 'dark, refined',
    ar: {
      about: 'عجوة المدينة من أرقى التمور السعودية وأكثرها تقديراً، وتُزرع في المدينة المنوّرة. تتميّز بلونها الداكن الذي يميل إلى الأسود وقوامها الطري ونكهتها المعتدلة الحلاوة، ولها مكانة ثقافية ودينية خاصة لدى كثيرين.',
      taste: 'حلاوة معتدلة وطعم غنيّ يشبه الزبيب مع لمسة خفيفة، وقوام طريّ متماسك — أقل حلاوة من السكري وأكثر عمقاً في النكهة.',
      uses: 'خيار مفضّل للإهداء وللمناسبات الخاصة، وتُقدَّم غالباً في العلب الفاخرة لمكانتها المميّزة.',
      store: 'تُحفظ في مكان بارد وجاف، ويُفضَّل حفظها في الثلاجة للحفاظ على طراوتها.',
      who: 'لمن يبحث عن تمر فاخر معتدل الحلاوة بمكانة خاصة، ولأرقى خيارات الإهداء.',
    },
    en: {
      about: 'Ajwa of Madinah is one of the finest and most esteemed Saudi date varieties, grown in Madinah. It is known for its dark, near-black colour, soft texture and mild sweetness, and holds a special cultural and religious significance for many.',
      taste: 'Mild sweetness with a rich, raisin-like depth and a soft yet firm texture — less sweet than Sukari and deeper in flavour.',
      uses: 'A favourite for gifting and special occasions, often presented in premium boxes for its distinguished standing.',
      store: 'Keep in a cool, dry place; refrigeration is preferred to maintain softness.',
      who: 'For those seeking a refined, mildly sweet date with special standing, and for the finest gifting.',
    },
  },
  majhool: {
    href: '/majhool-date',
    nameAr: 'تمر مجدول', nameEn: 'Majdool (Medjool) dates',
    tagAr: 'كبير وغني', tagEn: 'large, rich',
    ar: {
      about: 'يُلقَّب تمر المجدول (المجهول) بـ«ملك التمور» لحجمه الكبير وقوامه الغنيّ. يتميّز بلونه الكهرماني الداكن وحجمه اللافت ونكهته الغنية القريبة من التوفي.',
      taste: 'حلاوة غنية بطعم يشبه التوفي والكراميل، مع قوام لحميّ طريّ وحجم كبير يملأ اليد — تجربة فاخرة لكل قضمة.',
      uses: 'رائع للتقديم في الضيافة، أو محشوّاً بالمكسّرات، أو كهدية فاخرة بحجمه المبهر.',
      store: 'يُحفظ في مكان بارد وجاف، أو في الثلاجة للمحافظة على طراوته.',
      who: 'لمن يبحث عن تمر كبير غنيّ بطابع فاخر، ولمحبّي التمر اللحميّ الطري.',
    },
    en: {
      about: 'Majdool (Medjool) is nicknamed the “king of dates” for its large size and rich texture. It is known for its dark-amber colour, impressive size and rich, toffee-like flavour.',
      taste: 'Rich sweetness with a toffee-and-caramel note, a soft meaty texture and a large size that fills the hand — a luxurious bite.',
      uses: 'Excellent for hosting, stuffed with nuts, or as a premium gift thanks to its striking size.',
      store: 'Keep in a cool, dry place, or refrigerate to maintain softness.',
      who: 'For those who want a large, rich, premium date and lovers of a soft, meaty bite.',
    },
  },
  mabroom: {
    href: '/mabroom-date',
    nameAr: 'تمر مبروم', nameEn: 'Mabroom dates',
    tagAr: 'ممدود ومتماسك', tagEn: 'elongated, firm',
    ar: {
      about: 'يتميّز التمر المبروم بشكله الممدود الأنيق وقوامه المتماسك القابل للمضغ، ولونه البنّي الداكن. وهو من التمور الأقل رطوبة، ما يمنحه قواماً مطّاطياً مميّزاً وحلاوة معتدلة.',
      taste: 'حلاوة معتدلة وقوام متماسك مطّاطي بدل الطراوة الذائبة — مناسب لمن يفضّل تمراً «يُمضَغ» لا يذوب.',
      uses: 'خيار عملي كوجبة خفيفة أثناء التنقّل، ويتحمّل الحفظ لفترة أطول بفضل انخفاض رطوبته.',
      store: 'يُحفظ في مكان بارد وجاف؛ يبقى بحالة جيدة لفترة أطول من الأنواع الرطبة.',
      who: 'لمن يفضّل قواماً متماسكاً وحلاوة معتدلة، ولمن يريد تمراً يصمد في الحقيبة والمكتب.',
    },
    en: {
      about: 'Mabroom is known for its elegant elongated shape, firm chewy texture and dark-brown colour. It is one of the drier varieties, giving it a distinctive chewy bite and a mild sweetness.',
      taste: 'Mild sweetness with a firm, chewy texture rather than a melting softness — ideal for anyone who prefers a date to “chew” rather than dissolve.',
      uses: 'A practical on-the-go snack that keeps well for longer thanks to its lower moisture.',
      store: 'Keep in a cool, dry place; it stays good longer than the softer, wetter varieties.',
      who: 'For those who prefer a firm texture and mild sweetness, and want a date that survives the bag and the desk.',
    },
  },
  sagie: {
    href: '/sagie-date',
    nameAr: 'تمر صقعي', nameEn: 'Sagai (Sagie) dates',
    tagAr: 'بلونين وقوامين', tagEn: 'two-tone, two-texture',
    ar: {
      about: 'يتميّز التمر الصقعي بمظهره ثنائي اللون: طرف فاتح مقرمش وطرف داكن طريّ، ما يمنحه قوامين في ثمرة واحدة. وهو من التمور المتوازنة الحلاوة والمحبوبة في السعودية.',
      taste: 'توازن لطيف بين القرمشة والطراوة في القضمة الواحدة، مع حلاوة معتدلة غير مبالغ فيها.',
      uses: 'خيار يومي ممتاز للضيافة والتقديم مع القهوة، ويناسب من يحب تنوّع القوام.',
      store: 'يُحفظ في مكان بارد وجاف، أو في الثلاجة لإطالة طراوته.',
      who: 'لمن يحب الجمع بين القرمشة والطراوة، ولمن يفضّل حلاوة معتدلة متوازنة.',
    },
    en: {
      about: 'Sagai (Sagie) is known for its two-tone look: a lighter, crunchier end and a darker, softer end, giving two textures in one date. It is a balanced, well-loved variety in Saudi Arabia.',
      taste: 'A pleasant balance of crunch and softness in a single bite, with a moderate, not-overpowering sweetness.',
      uses: 'An excellent everyday choice for hosting and serving with coffee, and great for anyone who enjoys varied texture.',
      store: 'Keep in a cool, dry place, or refrigerate to extend its softness.',
      who: 'For those who like both crunch and softness, and prefer a balanced, moderate sweetness.',
    },
  },
};

// Map a product slug (e.g. "sukari-date-1kg") to a variety key.
export function varietyOf(slug) {
  const s = String(slug || '').toLowerCase();
  if (s.includes('sukari')) return 'sukari';
  if (s.includes('ajwa')) return 'ajwa';
  if (s.includes('majhool') || s.includes('majdool') || s.includes('medjool') || s.includes('majdoul')) return 'majhool';
  if (s.includes('mabroom') || s.includes('mabrom')) return 'mabroom';
  if (s.includes('sagie') || s.includes('saqi') || s.includes('sagai') || s.includes('saggai')) return 'sagie';
  return null;
}

// Build a richer per-product title + meta description than the old
// "تمرات - <name>" title (which carried no description or schema at all).
export function productSeo(pr, en) {
  const name = en ? (pr.name_en || pr.name_ar) : (pr.name_ar || pr.name_en);
  const v = VARIETIES[varietyOf(pr.slug)];
  if (en) {
    const tag = v ? ` — ${v.tagEn}` : '';
    const lead = v ? `${v.en.about} ` : '';
    return {
      title: `${name}${tag} | Tamrat`,
      description: `Buy ${name} online from Tamrat. ${lead}Fast delivery across Saudi Arabia, free shipping over 250 SAR.`.slice(0, 300),
    };
  }
  const tag = v ? ` — ${v.tagAr}` : '';
  const lead = v ? `${v.ar.taste} ` : '';
  return {
    title: `${name}${tag} | تمرات`,
    description: `اشترِ ${name} أونلاين من تمرات. ${lead}توصيل سريع لكل مناطق السعودية وشحن مجاني للطلبات فوق ٢٥٠ ر.س.`.slice(0, 300),
  };
}

// Product JSON-LD (GEO/rich-results). Now emitted in the MAIN (prerendered)
// render path — previously it only existed in the isLoading branch, so the
// baked HTML carried no Product schema at all.
export function productSchema(pr, en) {
  const name = en ? (pr.name_en || pr.name_ar) : (pr.name_ar || pr.name_en);
  const desc = (en ? (pr.description_en || pr.description_ar) : (pr.description_ar || pr.description_en)) || name;
  const canonical = `${SITE}/products/${pr.slug}`;
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name,
    image: pr.image_path,
    description: desc,
    sku: String(pr.slug),
    brand: { '@type': 'Brand', name: 'تمرات' },
    offers: {
      '@type': 'Offer',
      url: canonical,
      priceCurrency: 'SAR',
      price: Number(pr.price || 0).toFixed(2),
      availability: Number(pr.countInStock) > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    },
  };
  if (Number(pr.rating) > 0) {
    schema.aggregateRating = { '@type': 'AggregateRating', ratingValue: Number(pr.rating), reviewCount: Number(pr.numReviews) || 1 };
  }
  return schema;
}

const H2 = { color: '#7C9D64', fontWeight: 700, marginTop: '1.4rem' };
const LABEL = { color: '#7C9D64', fontWeight: 700 };

// EEAT body block rendered below the buy box on every product page.
export default function ProductEEAT({ product, en }) {
  const key = varietyOf(product && product.slug);
  const v = key && VARIETIES[key];
  if (!v) return null; // gift boxes / non-variety SKUs: no fabricated copy
  const c = en ? v.en : v.ar;
  const dir = en ? 'ltr' : 'rtl';
  const align = en ? 'left' : 'right';
  const name = en ? v.nameEn : v.nameAr;

  return (
    <section
      dir={dir}
      style={{ textAlign: align, maxWidth: 920, margin: '2.5rem auto 0', padding: '0 1rem', lineHeight: 1.9 }}
    >
      <h2 style={H2}>{en ? `About ${name}` : `عن ${name}`}</h2>
      <p>{c.about}</p>
      <p>{c.taste}</p>

      <h2 style={H2}>{en ? 'Best ways to enjoy' : 'أفضل طرق الاستمتاع به'}</h2>
      <p>{c.uses}</p>

      <p style={{ marginBottom: '0.4rem' }}>
        <span style={LABEL}>{en ? 'Storage: ' : 'الحفظ: '}</span>{c.store}
      </p>
      <p>
        <span style={LABEL}>{en ? 'Who it’s for: ' : 'لمن يناسب: '}</span>{c.who}
      </p>

      <p style={{ marginTop: '1.4rem' }}>
        <Link to={v.href} style={{ color: '#7C9D64', fontWeight: 700, textDecoration: 'underline' }}>
          {en ? `Learn more about ${name} →` : `اقرأ المزيد عن ${name} ←`}
        </Link>
      </p>
      <p>
        <Link to={'/best-saudi-dates'} style={{ color: '#7C9D64', fontWeight: 700, textDecoration: 'underline' }}>
          {en ? 'Guide: the best Saudi date varieties →' : 'دليل: أجود أنواع التمور السعودية ←'}
        </Link>
      </p>
    </section>
  );
}
